// BUG-2591: delete payment commit.
//
// handleDeletePayment (SuperMarket /pos, Estimate, PreOrder, Core usePaymentFlow) decides which payment
// entries go from the cart as it was when delete was clicked, then awaits a server reversal (gift card,
// credit, loyalty, wallet). Writing that click-time copy back after the await undid every cart change made
// while the call was pending (scanned items, qty edits, a payment added). This applies only the deletion to
// the cart as it is now.
import { trace } from "./debugTrace";
import { revertTenderRoundDown } from "./paymentRounding";

const sameLegacyEntry = (a, b) =>
  a?.name === b?.name &&
  String(a?.amount) === String(b?.amount) &&
  (a?.authorization || null) === (b?.authorization || null) &&
  (a?.transactionId || null) === (b?.transactionId || null);

// Entries of snapshotPayments the handler dropped: results[i] is the kept entry, or undefined when removed.
export const getRemovedPayments = (snapshotPayments, results) =>
  (snapshotPayments || []).filter((_, i) => results?.[i] === undefined);

// Removes each removed entry from livePayments: same object first, then the same paymentEntryId, then (for
// entries created before paymentEntryId existed) the same name / amount / authorization / transactionId.
export const removePaymentEntries = (livePayments, removedPayments) => {
  const remaining = [...(livePayments || [])];
  for (const removed of removedPayments || []) {
    let idx = remaining.indexOf(removed);
    if (idx < 0 && removed?.paymentEntryId) idx = remaining.findIndex((p) => p?.paymentEntryId === removed.paymentEntryId);
    if (idx < 0 && !removed?.paymentEntryId) idx = remaining.findIndex((p) => !p?.paymentEntryId && sameLegacyEntry(p, removed));
    if (idx >= 0) remaining.splice(idx, 1);
  }
  return remaining;
};

/**
 * Builds the cart to save after a payment delete, from the LIVE cart.
 * Returns null when the cart was replaced while the reversal was pending (another sOrderID: bill completed,
 * parked, cleared); the caller then writes nothing.
 *
 * options.tenderedFallbackZero: usePaymentFlow's old (tenderedAmount || 0) - amount; the other handlers
 * used tenderedAmount - amount. Kept as each caller had it.
 * options.clearGiftCardNumbers: SuperMarket cleared giftCardNumbers on a gift card delete.
 */
export const buildCartAfterPaymentDelete = (liveCart, snapshotCart, results, payment, options = {}) => {
  if (!liveCart) return null;
  if (snapshotCart?.sOrderID && liveCart.sOrderID !== snapshotCart.sOrderID) {
    trace("PaymentDelete", "cart replaced during reversal; delete not written", {
      snapshotSOrderID: snapshotCart.sOrderID,
      liveSOrderID: liveCart.sOrderID,
      payment: payment?.name,
      amount: payment?.amount,
    });
    console.warn("[BUG-2591] Cart changed to another bill while a payment reversal was pending; payment delete not written", {
      snapshotSOrderID: snapshotCart.sOrderID,
      liveSOrderID: liveCart.sOrderID,
    });
    return null;
  }
  const removed = getRemovedPayments(snapshotCart?.payments, results);
  const payments = removePaymentEntries(liveCart.payments, removed);
  let paid = 0;
  payments.forEach((p) => {
    paid = paid + parseFloat(p.amount);
  });
  // BUG-2612: deleting the Cash tender that rounded the bill down puts the total back.
  const next = { ...revertTenderRoundDown(liveCart, removed), payments, paid: paid + (liveCart?.advancePayment || 0) };
  const methodName = payment?.name?.toLowerCase();
  if (methodName === "cash") {
    next.tenderedAmount = (options.tenderedFallbackZero ? liveCart.tenderedAmount || 0 : liveCart.tenderedAmount) - payment.amount;
  } else if (methodName === "gift card" && options.clearGiftCardNumbers) {
    next.giftCardNumbers = [];
  }
  // handleLoyaltyPayment adds the refunded points to snapshotCart.customer in place. When the live cart holds a
  // different object for the same customer, carry that balance over so it is not lost.
  if (
    methodName === "loyalty" &&
    snapshotCart?.customer &&
    liveCart.customer &&
    liveCart.customer !== snapshotCart.customer &&
    liveCart.customer.cwrCustomerId === snapshotCart.customer.cwrCustomerId &&
    snapshotCart.customer.retlLoyaltyBalance !== undefined
  ) {
    next.customer = { ...liveCart.customer, retlLoyaltyBalance: snapshotCart.customer.retlLoyaltyBalance };
  }
  return next;
};
